import React from 'react';
import { Box, Grid, GridProps, Typography, keyframes } from '@mui/material';
import { alpha } from '@mui/material/styles';

/*****************************************************************************
 ** Design tokens shared by the player dashboard widgets.
 ** Colour is reserved for meaning: gain / loss / placement. Everything else is
 ** neutral so the numbers carry the page.
*****************************************************************************/

export const DS = {
    surface: '#1a222c',
    inset: 'rgba(145, 158, 171, 0.06)',
    border: 'rgba(145, 158, 171, 0.14)',
    borderStrong: 'rgba(145, 158, 171, 0.24)',

    text: '#f9fafb',
    textMuted: '#919eab',
    textFaint: '#637381',

    brand: '#00A76F',
    info: '#00B8D9',
    gain: '#22C55E',
    loss: '#FF5630',
    neutral: '#919eab',
    warning: '#FFAB00',
    violet: '#8E33FF',

    gold: '#FFC940',
    silver: '#C4CDD5',
    bronze: '#D98C4A',

    radius: '16px',
};

export const numericSx = {
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
};

export const trendColor = (value: number) =>
    value > 0 ? DS.gain : value < 0 ? DS.loss : DS.neutral;

export const formatSigned = (value: number, digits = 0) => {
    const rounded = Number(value.toFixed(digits));
    return `${rounded > 0 ? '+' : ''}${rounded}`;
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const enterSx = (delay = 0) => ({
    animation: `${fadeUp} 0.4s ease-out ${delay}s both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/*****************************************************************************
 ** Building blocks
*****************************************************************************/

export const Overline: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
    <Typography sx={{
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: color ?? DS.textMuted,
        lineHeight: 1.4,
    }}>
        {children}
    </Typography>
);

export const DeltaPill: React.FC<{ value: number; suffix?: string; digits?: number }> = ({ value, suffix, digits = 0 }) => {
    const color = trendColor(value);
    return (
        <Box component="span" sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: '2px',
            borderRadius: '999px',
            background: alpha(color, 0.14),
            color,
            fontSize: '12px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            ...numericSx,
        }}>
            {formatSigned(value, digits)}{suffix ? ` ${suffix}` : ''}
        </Box>
    );
};

export const Meter: React.FC<{ value: number; color: string; height?: number }> = ({ value, color, height = 6 }) => (
    <Box sx={{ height, borderRadius: '999px', background: DS.inset, overflow: 'hidden' }}>
        <Box sx={{
            height: '100%',
            width: `${Math.max(0, Math.min(value, 1)) * 100}%`,
            borderRadius: '999px',
            background: color,
            transition: 'width 0.6s ease-out',
        }} />
    </Box>
);

interface PanelProps {
    title?: string;
    subtitle?: React.ReactNode;
    action?: React.ReactNode;
    children: React.ReactNode;
    grid?: Pick<GridProps, 'xs' | 'sm' | 'md' | 'lg'>;
    delay?: number;
    noPadding?: boolean;
}

export const Panel: React.FC<PanelProps> = ({ title, subtitle, action, children, grid = { xs: 12 }, delay = 0, noPadding }) => (
    <Grid item {...grid}>
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: DS.surface,
            border: `1px solid ${DS.border}`,
            borderRadius: DS.radius,
            overflow: 'hidden',
            ...enterSx(delay),
        }}>
            {title && (
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    pt: { xs: 2, sm: 2.5 },
                    pb: noPadding ? 2 : 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '16px', letterSpacing: '-0.01em', color: DS.text }}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.25 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {action}
                </Box>
            )}
            <Box sx={{ flex: 1, ...(noPadding ? {} : { px: { xs: 2, sm: 3 }, pt: 2, pb: { xs: 2, sm: 3 } }) }}>
                {children}
            </Box>
        </Box>
    </Grid>
);

export const PageHeader: React.FC<{ overline: string; title: string; subtitle?: React.ReactNode; action?: React.ReactNode }> = ({ overline, title, subtitle, action }) => (
    <Grid item xs={12} sx={enterSx(0)}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
                <Overline>{overline}</Overline>
                <Typography sx={{ fontSize: { xs: '24px', sm: '28px' }, fontWeight: 800, letterSpacing: '-0.02em', color: DS.text, lineHeight: 1.2 }}>
                    {title}
                </Typography>
                {subtitle && <Typography sx={{ fontSize: '14px', color: DS.textMuted, mt: 0.5 }}>{subtitle}</Typography>}
            </Box>
            {action}
        </Box>
    </Grid>
);
